import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  cardClass,
  cardBodyClass,
  btnPrimaryClass,
  btnSecondaryClass,
  badgeClass,
  modalOverlayClass,
  modalPanelClass,
  modalHeaderClass,
  modalBodyClass,
  modalFooterClass,
  cardTitleClass,
} from "./dashboardStyles";
import { OpenProfessionalAccountAction } from "../../store/action";
import { ACCESS_TOKEN } from "../../constants/commonConst";
import { notifySaveError } from "../../utils/notify";

/** What a business account adds, in the order a salon owner would ask about it. */
const BENEFITS = [
  { key: "bookingPage", icon: "bi-globe2" },
  { key: "calendar", icon: "bi-calendar-week" },
  { key: "reminders", icon: "bi-bell" },
  { key: "reports", icon: "bi-graph-up-arrow" },
];

// Auto-fit on the card's own width, not the viewport: from 1024px the sidebar
// takes 280px and a viewport breakpoint would squeeze two columns into ~350px.
const benefitGridClass =
  "grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(240px,100%),1fr))]";

const closeButtonClass =
  "flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-lg text-ink-500 transition hover:bg-ink-100 hover:text-ink-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600";

/**
 * "Open a professional account", in Settings, for personal (customer) accounts.
 *
 * Someone who joined to book an appointment — through Google from the login
 * page, or the personal option on signup — can turn the same account into a
 * business owner's, and set the business up in the wizard straight after.
 *
 * The confirmation says plainly what changes, because one thing does that a
 * customer would not guess: until the business is set up, "My bookings" and
 * "Book" are among the tabs the wizard locks (GATED_SETUP_ITEM_IDS). The
 * bookings themselves are untouched. One-way: there is no button back.
 *
 * The API answers with a new token, since the role lives in it. The page is
 * then reloaded at the wizard rather than navigated to: the dashboard reads the
 * role once, when it mounts, and an in-app navigation would keep the customer's
 * sidebar beside the business's wizard.
 */
const ProfessionalAccountPanel = () => {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!confirmOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape" && !submitting) setConfirmOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [confirmOpen, submitting]);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const { accessToken } = await OpenProfessionalAccountAction();
      localStorage.setItem(ACCESS_TOKEN, accessToken);
      window.location.assign("/dashboard/organization");
    } catch (err) {
      notifySaveError(err, t("Dashboard.Settings.professional.failed"));
      setSubmitting(false);
    }
  };

  const close = () => {
    if (!submitting) setConfirmOpen(false);
  };

  return (
    <section
      className={`${cardClass} professional-account-panel`}
      aria-labelledby="professional-account-title"
    >
      <div className="border-b border-ink-200 bg-linear-to-br from-brand-50 via-white to-white px-5 py-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-brand-600 text-white shadow-brand">
            <i className="bi bi-briefcase text-xl" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <span className={`${badgeClass} bg-brand-100 text-brand-800`}>
              {t("Dashboard.Settings.professional.badge")}
            </span>
            <h3
              id="professional-account-title"
              className="m-0 mt-2 text-lg font-bold tracking-tight text-ink-900"
            >
              {t("Dashboard.Settings.professional.title")}
            </h3>
            <p className="m-0 mt-1 text-fluid-sm leading-relaxed text-ink-600">
              {t("Dashboard.Settings.professional.description")}
            </p>
          </div>
        </div>
      </div>

      <div className={cardBodyClass}>
        <ul className={`${benefitGridClass} m-0 list-none p-0`}>
          {BENEFITS.map(({ key, icon }) => (
            <li key={key} className="flex min-w-0 items-start gap-3 rounded-lg border border-ink-200 p-3.5">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                <i className={`bi ${icon}`} aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="m-0 text-sm font-semibold text-ink-900">
                  {t(`Dashboard.Settings.professional.benefits.${key}.title`)}
                </p>
                <p className="m-0 mt-0.5 text-xs leading-relaxed text-ink-500">
                  {t(`Dashboard.Settings.professional.benefits.${key}.body`)}
                </p>
              </div>
            </li>
          ))}
        </ul>

        <p className="m-0 mt-4 flex items-start gap-2 text-fluid-sm text-ink-600">
          <i className="bi bi-shield-check mt-0.5 text-brand-600" aria-hidden="true" />
          <span className="min-w-0">{t("Dashboard.Settings.professional.keeps")}</span>
        </p>

        <button
          type="button"
          className={`${btnPrimaryClass} mt-5 min-h-11 w-full sm:w-auto`}
          onClick={() => setConfirmOpen(true)}
        >
          {t("Dashboard.Settings.professional.open")}
          <i className="bi bi-arrow-right" aria-hidden="true" />
        </button>
      </div>

      {confirmOpen && (
        <div className={modalOverlayClass} onClick={close}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="professional-account-confirm-title"
            className={modalPanelClass}
            onClick={(event) => event.stopPropagation()}
          >
            <div className={modalHeaderClass}>
              <h3 id="professional-account-confirm-title" className={cardTitleClass}>
                {t("Dashboard.Settings.professional.confirmTitle")}
              </h3>
              {/* modalCloseClass is 32px; spelled out at 44px for touch rather
                  than appending size-11, which would collide with its size-8. */}
              <button
                type="button"
                className={closeButtonClass}
                onClick={close}
                aria-label={t("Dashboard.Settings.professional.close")}
              >
                <i className="bi bi-x text-xl" aria-hidden="true" />
              </button>
            </div>

            <div className={modalBodyClass}>
              <p className="m-0 text-fluid-sm leading-relaxed text-ink-600">
                {t("Dashboard.Settings.professional.confirmIntro")}
              </p>

              <ul className="m-0 mt-4 list-none space-y-3 p-0">
                <li className="flex items-start gap-3 text-fluid-sm text-ink-700">
                  <i className="bi bi-grid-1x2 mt-0.5 text-brand-600" aria-hidden="true" />
                  <span className="min-w-0">{t("Dashboard.Settings.professional.changeDashboard")}</span>
                </li>
                <li className="professional-account-locked flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-fluid-sm text-ink-700">
                  <i className="bi bi-lock mt-0.5 text-amber-600" aria-hidden="true" />
                  <span className="min-w-0">{t("Dashboard.Settings.professional.changeLocked")}</span>
                </li>
                <li className="flex items-start gap-3 text-fluid-sm text-ink-700">
                  <i className="bi bi-person-check mt-0.5 text-brand-600" aria-hidden="true" />
                  <span className="min-w-0">{t("Dashboard.Settings.professional.changeKeeps")}</span>
                </li>
                <li className="flex items-start gap-3 text-fluid-sm text-ink-700">
                  <i className="bi bi-arrow-right-circle mt-0.5 text-brand-600" aria-hidden="true" />
                  <span className="min-w-0">{t("Dashboard.Settings.professional.changeOneWay")}</span>
                </li>
              </ul>
            </div>

            <div className={modalFooterClass}>
              <button
                type="button"
                className={`${btnSecondaryClass} min-h-11`}
                onClick={close}
                disabled={submitting}
              >
                {t("Dashboard.Settings.professional.cancel")}
              </button>
              <button
                type="button"
                className={`${btnPrimaryClass} min-h-11`}
                onClick={handleConfirm}
                disabled={submitting}
              >
                {submitting
                  ? t("Dashboard.Settings.professional.opening")
                  : t("Dashboard.Settings.professional.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ProfessionalAccountPanel;
